import {
  buildProductIdentityFromItem,
  type ProductIdentityContext,
} from '../../components/Trackable/buildProductIdentity';
import {
  applyProductContext,
  resolveConfiguredCustomDimensions,
} from '../configurable-step/applyProductContext';
import { listProductFieldFallbacks } from '../configurable-step/resolveProductFields';
import type {
  CustomDimensionMapping,
  ProductFieldMapping,
} from '../configurable-step/productEventMappingContract';
import { isOfferingResultsCardFormatSubtype } from '../configurable-step/offeringsResultsSourceIdentity';
import { isPii } from '../isPii';
import type { Step } from '../../types/types';

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

export interface FieldTrackingMeta {
  fieldName?: string;
  fieldType?: string;
  leadFieldAssociated?: string;
}

function containsPasswordKey(key: string | undefined): boolean {
  return typeof key === 'string' && key.toLowerCase().includes('password');
}

// PII-safe field value shared by FieldViewed/FieldInputted: `''` when empty,
// `'SANITIZED'` when the authored input type is password, any identifier
// contains "password", or the chosen lead field is PII. Otherwise the raw value.
export function sanitizeFieldValue(
  raw: unknown,
  fieldMeta: FieldTrackingMeta,
  formKey: string,
  currentStep: Step | undefined,
  inputType?: string,
): string {
  const value = typeof raw === 'string' ? raw : '';
  if (value.length === 0) return '';
  if (inputType === 'password') return 'SANITIZED';
  if (
    containsPasswordKey(fieldMeta.leadFieldAssociated) ||
    containsPasswordKey(fieldMeta.fieldName) ||
    containsPasswordKey(formKey)
  ) {
    return 'SANITIZED';
  }
  const leadField =
    fieldMeta.leadFieldAssociated ?? fieldMeta.fieldName ?? formKey;
  return isPii(leadField, currentStep) ? 'SANITIZED' : value;
}

export interface ResolveCardEventingParams {
  item: unknown;
  itemIndexPath: Parameters<typeof buildProductIdentityFromItem>[1];
  clickKind: string | undefined;
  clickFormatSubtype: string | undefined;
  fieldMeta: FieldTrackingMeta | null;
  formKey: string | undefined;
  formStateValue: unknown;
  currentStep: Step | undefined;
  /**
   * Authored component `type` prop (for example HEN `Input` `type`).
   * `'password'` redacts a non-empty value even when the key is not
   * password-like. Read the prop, not the live DOM type.
   */
  inputType?: string;
  isProgramSubmitted?: (programId: number) => boolean;
  /**
   * Dual-shape `tracking.productFields` from the current step's DSL
   * root. Overlay onto {@link buildProductIdentityFromItem} using
   * the repeater `item` as the resolve root. Missing/empty = no-op.
   */
  productFields?: Readonly<Record<string, ProductFieldMapping>>;
  /**
   * Sibling `tracking.customDimensions` mappings. Overlay onto
   * identity as `mappedCustomDimensions` using the repeater `item`
   * as the resolve root. Missing/empty = no-op.
   */
  customDimensionMappings?: readonly CustomDimensionMapping[];
}

export interface CardEventing {
  productContext: ProductIdentityContext | undefined;
  resultActionOutcome: string | undefined;
  fieldViewValue: string | undefined;
}

/**
 * Overlay `tracking.productFields` onto identity from
 * {@link buildProductIdentityFromItem}. No map leaves identity
 * bit-identical (same reference).
 *
 * @param {ProductIdentityContext | undefined} identity - Repeater identity base
 * @param {Readonly<Record<string, ProductFieldMapping>> | undefined} productFields - DSL map
 * @param {unknown} item - Repeater item used as the resolve root
 * @returns {ProductIdentityContext | undefined} Identity with mapped `product` overlay
 */
function overlayMappedProductFields(
  identity: ProductIdentityContext | undefined,
  productFields: Readonly<Record<string, ProductFieldMapping>> | undefined,
  item: unknown,
): ProductIdentityContext | undefined {
  if (!productFields || Object.keys(productFields).length === 0) {
    return identity;
  }
  const root = isPlainObject(item) ? item : undefined;
  const fallbacks = listProductFieldFallbacks(productFields, root);
  const mapped = applyProductContext({
    map: productFields,
    root,
    baseProduct: identity?.product,
  });
  const withFallbacks = (base: ProductIdentityContext | undefined) => {
    if (fallbacks.length === 0) return base;
    return { ...(base ?? {}), productFieldFallbacks: fallbacks };
  };
  if (!mapped) return withFallbacks(identity);
  if (!identity) return withFallbacks({ product: mapped });
  if (identity.product === mapped && fallbacks.length === 0) return identity;
  return withFallbacks({ ...identity, product: mapped });
}

/**
 * Attach resolved config dims onto identity as
 * `mappedCustomDimensions`. Payload builders overlay them onto
 * platform dims. No mappings leaves identity unchanged (same
 * reference).
 *
 * @param {ProductIdentityContext | undefined} identity - Repeater identity base
 * @param {readonly CustomDimensionMapping[] | undefined} mappings - DSL mappings
 * @param {unknown} item - Repeater item used as the resolve root
 * @returns {ProductIdentityContext | undefined} Identity with mapped dim overlay
 */
function overlayMappedCustomDimensions(
  identity: ProductIdentityContext | undefined,
  mappings: readonly CustomDimensionMapping[] | undefined,
  item: unknown,
): ProductIdentityContext | undefined {
  const mapped = resolveConfiguredCustomDimensions(
    mappings,
    isPlainObject(item) ? item : undefined,
  );
  if (mapped.length === 0) return identity;
  if (!identity) return { mappedCustomDimensions: mapped };
  return { ...identity, mappedCustomDimensions: mapped };
}

// Pure card-level eventing decisions, lifted out of `createBoundRender` so it
// stays focused on prop-bag assembly and this stays unit-testable.
export function resolveCardEventing({
  item,
  itemIndexPath,
  clickKind,
  clickFormatSubtype,
  fieldMeta,
  formKey,
  formStateValue,
  currentStep,
  inputType,
  isProgramSubmitted,
  productFields,
  customDimensionMappings,
}: ResolveCardEventingParams): CardEventing {
  const productContext =
    clickKind === 'product'
      ? overlayMappedCustomDimensions(
          overlayMappedProductFields(
            buildProductIdentityFromItem(item, itemIndexPath),
            productFields,
            item,
          ),
          customDimensionMappings,
          item,
        )
      : undefined;

  const resultActionOutcome = ((): string | undefined => {
    if (!productContext) return undefined;
    const effectiveFormatSubtype =
      clickFormatSubtype ?? productContext.formatSubtype;
    if (!isOfferingResultsCardFormatSubtype(effectiveFormatSubtype)) {
      return undefined;
    }
    const programId =
      isPlainObject(item) && typeof item.programId === 'number'
        ? item.programId
        : undefined;
    const isSubmitted =
      programId !== undefined && isProgramSubmitted?.(programId) === true;
    return isSubmitted ? 'application' : 'explore';
  })();

  const fieldViewValue =
    fieldMeta && formKey !== undefined
      ? sanitizeFieldValue(
          formStateValue,
          fieldMeta,
          formKey,
          currentStep,
          inputType,
        )
      : undefined;

  return { productContext, resultActionOutcome, fieldViewValue };
}
